(() => {
  "use strict";

  document.documentElement.classList.add("js");

  const query = (selector, root = document) => root.querySelector(selector);
  const queryAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  function installImageFallbacks() {
    queryAll("img[data-vc-fallback]").forEach(image => {
      image.addEventListener("error", () => {
        if (image.dataset.vcFallback && image.src !== image.dataset.vcFallback) image.src = image.dataset.vcFallback;
      }, { once: true });
    });
  }

  function installDesktopMegaMenus() {
    const menus = queryAll(".desktop-navigation .services-menu");
    const desktop = window.matchMedia("(min-width: 761px)");
    if (!menus.length) return;
    const close = (menu, restoreFocus = false, dismissHover = false) => {
      const trigger = query(".services-menu-trigger", menu);
      const panel = query(".services-menu-panel", menu);
      menu.classList.remove("is-open");
      if (dismissHover) menu.classList.add("is-dismissed");
      trigger?.setAttribute("aria-expanded", "false");
      panel?.setAttribute("aria-hidden", "true");
      if (panel) panel.inert = true;
      if (restoreFocus && trigger) trigger.focus({ preventScroll: true });
    };
    const closeOthers = active => menus.forEach(menu => { if (menu !== active) close(menu); });
    const open = menu => {
      closeOthers(menu);
      menu.classList.remove("is-dismissed");
      menu.classList.add("is-open");
      query(".services-menu-trigger", menu)?.setAttribute("aria-expanded", "true");
      const panel = query(".services-menu-panel", menu);
      panel?.setAttribute("aria-hidden", "false");
      if (panel) panel.inert = false;
    };

    desktop.addEventListener("change", () => menus.forEach(menu => close(menu, false, true)));
    menus.forEach(menu => {
      const trigger = query(".services-menu-trigger", menu);
      const panel = query(".services-menu-panel", menu);
      if (!trigger || !panel) return;
      trigger.addEventListener("click", () => {
        menu.classList.contains("is-open") ? close(menu, false, true) : open(menu);
      });
      trigger.addEventListener("keydown", event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          trigger.click();
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          open(menu);
          query("a[href]", panel)?.focus();
        } else if (event.key === "Escape") {
          event.preventDefault();
          close(menu, true, true);
        }
      });
      panel.addEventListener("keydown", event => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        close(menu, true, true);
      });
      menu.addEventListener("focusout", event => {
        if (event.relatedTarget && menu.contains(event.relatedTarget)) return;
        close(menu);
      });
      menu.addEventListener("pointerleave", event => {
        if (event.pointerType === "touch") return;
        menu.classList.remove("is-dismissed");
        if (!menu.contains(document.activeElement)) close(menu);
      });
      menu.addEventListener("pointerenter", event => {
        if (event.pointerType === "touch") return;
        open(menu);
      });
    });
    document.addEventListener("click", event => menus.forEach(menu => {
      if (!menu.contains(event.target)) close(menu);
    }));
  }

  function installMobileNavigation() {
    queryAll(".mobile-menu").forEach(menu => {
      const trigger = query(":scope > summary", menu);
      const panel = query(".mobile-menu-panel", menu);
      if (!trigger) return;
      const sync = () => {
        trigger.setAttribute("aria-expanded", String(menu.open));
        trigger.setAttribute("aria-label", menu.open ? "Close navigation menu" : "Open navigation menu");
        document.documentElement.classList.toggle("mobile-nav-open", menu.open);
      };
      window.matchMedia("(min-width: 761px)").addEventListener("change", () => { menu.open = false; sync(); });
      menu.addEventListener("toggle", sync);
      menu.addEventListener("keydown", event => {
        if (!menu.open) return;
        if (event.key === "Escape") {
          event.preventDefault();
          menu.open = false;
          trigger.focus({ preventScroll: true });
          return;
        }
        if (event.key !== "Tab" || !panel) return;
        const focusable = queryAll('a[href], summary, button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
          .filter(element => !element.closest("[hidden]") && element.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          trigger.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          trigger.focus({ preventScroll: true });
        }
      });
      queryAll(".mobile-nav-section", menu).forEach(section => {
        section.addEventListener("toggle", () => {
          if (!section.open) return;
          queryAll(".mobile-nav-section", menu).forEach(other => {
            if (other !== section) other.open = false;
          });
        });
      });
      sync();
    });
  }

  function markCurrentNavigation() {
    const currentPath = window.location.pathname.replace(/\/$/, "") || "/";
    queryAll(".site-header a[href]").forEach(link => {
      let path;
      try {
        path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, "") || "/";
      } catch {
        return;
      }
      if (path === currentPath) link.setAttribute("aria-current", "page");
    });
  }

  function optimizeSiteConversions() {
    // Every conversion path should arrive at the short project form, not the contact-page heading.
    queryAll('a[href="/pages/contact"]').forEach(link => {
      link.href = "/pages/contact#project-form";
    });

  }

  function analyticsAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.analyticsProcessingAllowed) {
      try { return privacy.analyticsProcessingAllowed() === true; } catch { return false; }
    }
    return document.documentElement.dataset.vcAnalyticsConsent === "granted";
  }

  function marketingAllowed() {
    try { return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true; } catch { return false; }
  }

  function eventHref(href = "") {
    if (!href) return "";
    try {
      const url = new URL(href, window.location.origin);
      if (["mailto:", "tel:"].includes(url.protocol)) return url.protocol;
      if (!["https:", "http:"].includes(url.protocol)) return "";
      return `${url.origin === window.location.origin ? "" : url.origin}${url.pathname}`;
    } catch { return ""; }
  }

  const ANALYTICS = 1, MARKETING = 2;
  function pushEvent(event, label, extra = {}, sent = 0) {
    const payload = { vc_label: /^(email|phone)_click$/.test(event) ? "contact" : label || "", vc_path: window.location.pathname, ...extra };
    if ("vc_href" in payload) payload.vc_href = eventHref(payload.vc_href);
    let delivered = sent;
    if (!(sent & ANALYTICS) && analyticsAllowed()) {
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", event, payload);
          delivered |= ANALYTICS;
        } else if (Array.isArray(window.dataLayer) && Object.keys(window.google_tag_manager || {}).some(id => /^GTM-[A-Z0-9]+$/.test(id))) {
          window.dataLayer.push({ event, ...payload });
          delivered |= ANALYTICS;
        }
      } catch {}
      if (delivered & ANALYTICS) {
        try { window.dispatchEvent(new CustomEvent("vivekcodes:conversion", { detail: { event, ...payload } })); } catch {}
      }
    }
    if (!(sent & MARKETING) && marketingAllowed() && typeof window.fbq === "function") {
      try {
        window.fbq("trackCustom", event, payload);
        delivered |= MARKETING;
      } catch {}
    }
    return delivered;
  }

  const leadMeasurements = new Map();
  const submittingLeadForms = new Set();
  function leadKey(label) {
    return `vc-lead:${window.location.pathname}:${label}`;
  }

  function leadState(label, value) {
    const key = leadKey(label);
    if (value !== undefined) {
      leadMeasurements.set(key, value);
      try { window.sessionStorage.setItem(key, String(value)); } catch {}
    } else if (!leadMeasurements.has(key)) {
      let stored = 0;
      try { stored = Number(window.sessionStorage.getItem(key)) & (ANALYTICS | MARKETING); } catch {}
      leadMeasurements.set(key, stored);
    }
    return leadMeasurements.get(key);
  }

  function formLabel(form) {
    return form.dataset?.vcFormLabel || form.id || "contact_form";
  }

  function recordEnquirySubmission(form) {
    const label = formLabel(form);
    submittingLeadForms.add(leadKey(label));
    if (analyticsAllowed() || marketingAllowed()) leadState(label, 0);
    pushEvent("contact_form_submit", label);
  }

  function installConfirmedLeadTracking() {
    const successes = queryAll("[data-vc-form-success]");
    if (!successes.length) return;
    const retry = () => {
      if (!analyticsAllowed() && !marketingAllowed()) return;
      successes.forEach(success => {
        const label = success.dataset.vcLabel || "contact_form";
        if (submittingLeadForms.has(leadKey(label))) return;
        const state = leadState(label);
        const sent = pushEvent("generate_lead", label, {}, state);
        if (sent !== state) leadState(label, sent);
      });
    };
    let timers = [];
    const checkReadiness = () => {
      timers.forEach(timer => window.clearTimeout(timer));
      retry();
      timers = [250, 1000, 3000, 10000].map(delay => window.setTimeout(retry, delay));
    };
    document.addEventListener("visitorConsentCollected", checkReadiness);
    document.addEventListener("load", event => { if (event.target?.tagName === "SCRIPT") retry(); }, true);
    window.addEventListener("pageshow", retry);
    checkReadiness();
  }

  function installAnalyticsHooks() {
    document.addEventListener("click", event => {
      const target = event.target.closest("[data-vc-event]");
      if (target) {
        if (target.matches('[type="submit"]') || ["lead_submit", "contact_form_completion", "generate_lead"].includes(target.dataset.vcEvent)) return;
        pushEvent(target.dataset.vcEvent, target.dataset.vcLabel, { vc_href: target.getAttribute("href") || "" });
        return;
      }
      const link = event.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      let name;
      if (href.includes("wa.me/")) name = "whatsapp_click";
      else if (href.startsWith("mailto:")) name = "email_click";
      else if (href.startsWith("tel:")) name = "phone_click";
      else if (href.includes("/pages/contact") || href.includes("/pages/request-a-proposal")) {
        name = link.closest(".site-header") ? "header_cta_click" : link.closest(".agency-page-hero,.agency-hero") ? "hero_cta_click" : "service_cta_click";
      }
      else if (href.includes("/pages/work-")) name = "case_study_click";
      else if (link.closest(".agency-link-grid,.agency-mega-menu")) name = "service_cta_click";
      if (name) pushEvent(name, link.textContent.trim(), { vc_href: href });
    });

    queryAll(".agency-form").forEach(form => {
      let started = false;
      form.addEventListener("input", event => {
        if (started || !event.target.matches("input, select, textarea") || event.target.matches(".vc-honeypot")) return;
        started = Boolean(pushEvent("contact_form_start", formLabel(form)));
      });
    });
    installConfirmedLeadTracking();
  }

  function installProjectDateInputs() {
    const fields = queryAll('.agency-form input[type="date"]');
    if (!fields.length) return;
    let midnightTimer;
    const refresh = () => {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      fields.forEach(field => { field.min = today; });
      const start = query("#project-start");
      const launch = query("#project-timeline");
      if (start?.value && launch && start.value > today) launch.min = start.value;
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      window.clearTimeout(midnightTimer);
      midnightTimer = window.setTimeout(refresh, midnight.getTime() - now.getTime());
    };
    refresh();
    fields.forEach(field => {
      field.addEventListener("focus", refresh);
      field.addEventListener("input", refresh);
    });
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) refresh();
    });
  }

  function installCustomBudgets() {
    queryAll(".agency-form").forEach(form => {
      const select = query("[data-budget-select]", form);
      const container = query("[data-custom-budget]", form);
      if (!select || !container) return;
      const input = query("input", container);
      const option = query('option[value="Custom budget"]', select);
      if (!input || !option) return;
      const refresh = () => {
        const custom = select.value === option.value;
        container.hidden = !custom;
        input.disabled = !custom;
        input.required = custom;
      };
      select.addEventListener("change", () => {
        refresh();
        if (!input.disabled) input.focus();
      });
      form.addEventListener("reset", () => window.setTimeout(refresh, 0));
      window.addEventListener("pageshow", refresh);
      option.disabled = false;
      refresh();
    });
  }

  function installEnquiryVisibility() {
    if (!("IntersectionObserver" in window)) return;
    const visible = new Set();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      document.documentElement.classList.toggle("vc-enquiry-in-view", visible.size > 0);
    });
    queryAll(".agency-form").forEach(form => observer.observe(form));
  }

  function installFormSubmissionProtection() {
    queryAll(".agency-form").forEach(form => {
      if (form.classList.contains("agency-contact-form")) return;
      form.noValidate = true;
      let submitted = false;
      const button = query('button[type="submit"]', form);
      const status = query("[data-form-status]", form);
      const originalContent = button?.innerHTML;
      const fields = queryAll("input, select, textarea", form).filter(field => field.willValidate);
      const fieldKey = field => (field.name || field.id || field.type).replace(/^contact\[|\]$/g, "").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      const clearFieldError = field => {
        const id = field.dataset.formErrorId;
        if (id) document.getElementById(id)?.remove();
        field.removeAttribute("aria-invalid");
        const describedBy = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(item => item && item !== id);
        if (describedBy.length) field.setAttribute("aria-describedby", describedBy.join(" "));
        else field.removeAttribute("aria-describedby");
        delete field.dataset.formErrorId;
      };
      const showFieldError = field => {
        clearFieldError(field);
        const id = `${form.id}-${fieldKey(field)}-error`;
        const error = document.createElement("small");
        error.id = id;
        error.className = "agency-field-error";
        error.textContent = field.validationMessage || "Please complete this field.";
        (field.closest("label") || field.parentElement)?.append(error);
        field.setAttribute("aria-invalid", "true");
        field.setAttribute("aria-describedby", [field.getAttribute("aria-describedby"), id].filter(Boolean).join(" "));
        field.dataset.formErrorId = id;
        return error.textContent;
      };
      const setStatus = (message, state = "progress") => {
        if (!status) return;
        status.textContent = message;
        status.dataset.state = state;
      };
      fields.forEach(field => {
        field.addEventListener("input", () => {
          clearFieldError(field);
          if (field.checkValidity()) setStatus("");
        });
        field.addEventListener("change", () => {
          clearFieldError(field);
          if (field.checkValidity()) setStatus("");
        });
      });
      window.addEventListener("pageshow", event => {
        if (!event.persisted) return;
        submitted = false;
        if (button) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
          button.innerHTML = originalContent;
        }
      });
      form.addEventListener("submit", event => {
        if (query(".vc-honeypot", form)?.value || submitted) {
          event.preventDefault();
          return;
        }
        const invalidFields = fields.filter(field => !field.checkValidity());
        const formIsValid = typeof form.checkValidity !== "function" || form.checkValidity();
        if (!formIsValid || invalidFields.length) {
          event.preventDefault();
          invalidFields.forEach(showFieldError);
          const firstInvalid = invalidFields[0];
          if (firstInvalid) {
            const details = firstInvalid.closest("details");
            if (details) details.open = true;
            setStatus(`Please review the form. ${firstInvalid.validationMessage || "A required field needs attention."}`, "error");
            pushEvent("contact_form_validation_error", form.dataset.vcFormLabel || form.id || "project_form", { vc_field: fieldKey(firstInvalid) });
            firstInvalid.focus({ preventScroll: true });
            firstInvalid.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "center" });
          } else {
            setStatus("Please review the form. A required field needs attention.", "error");
            pushEvent("contact_form_validation_error", form.dataset.vcFormLabel || form.id || "project_form", { vc_field: "form" });
          }
          return;
        }
        recordEnquirySubmission(form);
        submitted = true;
        if (button) {
          button.disabled = true;
          button.setAttribute("aria-busy", "true");
          button.textContent = "Sending...";
        }
        setStatus("Sending your project enquiry.", "pending");
      });
    });
  }

  function installAgencyAppDirectory() {
    const directory = query("[data-agency-app-directory]");
    if (!directory) return;
    const input = query("[data-app-search]", directory);
    const buttons = queryAll("[data-app-filter]", directory);
    const cards = queryAll("[data-app-card]", directory);
    let category = "all";
    let searchTerm = "";

    const refresh = () => {
      let visible = 0;
      cards.forEach(card => {
        const categories = (card.dataset.category || "").split(/\s+/);
        const matchesCategory = category === "all" || categories.includes(category);
        const matchesSearch = !searchTerm || card.textContent.toLowerCase().includes(searchTerm);
        card.hidden = !(matchesCategory && matchesSearch);
        if (!card.hidden) visible += 1;
      });
      directory.dataset.resultCount = String(visible);
    };

    buttons.forEach(button => button.addEventListener("click", () => {
      category = button.dataset.appFilter || "all";
      buttons.forEach(item => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      refresh();
    }));
    buttons.forEach(button => button.setAttribute("aria-pressed", String(button.classList.contains("is-active"))));
    input?.addEventListener("input", () => {
      searchTerm = input.value.trim().toLowerCase();
      refresh();
    });
    refresh();
  }

  function installAgencyContactForm() {
    const form = query(".agency-contact-form");
    if (!form) return;
    form.noValidate = true;
    const steps = queryAll("[data-form-step]", form);
    const progressText = query("[data-form-progress-text]", form);
    const progressBar = query("[data-form-progress-bar]", form);
    const status = query("[data-form-status]", form);
    let current = 0;
    let submitted = false;

    const fieldKey = field => (field.name || field.id || field.type).replace(/^contact\[|\]$/g, "").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    const errorContainer = field => field.type === "radio" ? field.closest(".agency-form-model") : field.closest("label") || field.parentElement;
    const relatedFields = field => field.type === "radio"
      ? queryAll("input", form).filter(item => item.name === field.name)
      : [field];
    const clearFieldError = field => {
      const key = field.dataset.formErrorId;
      if (key) document.getElementById(key)?.remove();
      field.removeAttribute("aria-invalid");
      const hints = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== key);
      if (hints.length) field.setAttribute("aria-describedby", hints.join(" "));
      else field.removeAttribute("aria-describedby");
      delete field.dataset.formErrorId;
    };
    const clearRelatedErrors = field => relatedFields(field).forEach(clearFieldError);
    const setStatus = (message, focus = false, state = "progress") => {
      if (!status) return;
      status.textContent = message;
      status.dataset.state = state;
      if (focus) status.focus({ preventScroll: true });
    };
    const showFieldError = field => {
      const related = relatedFields(field);
      clearRelatedErrors(field);
      const id = `${form.id}-${fieldKey(field)}-error`;
      const error = document.createElement("small");
      error.id = id;
      error.className = "agency-field-error";
      error.textContent = field.validationMessage || "Please complete this field.";
      errorContainer(field)?.append(error);
      related.forEach(item => {
        item.setAttribute("aria-invalid", "true");
        item.setAttribute("aria-describedby", [item.getAttribute("aria-describedby"), id].filter(Boolean).join(" "));
        item.dataset.formErrorId = id;
      });
      return error.textContent;
    };

    const show = (index, moveFocus = true) => {
      current = Math.max(0, Math.min(steps.length - 1, index));
      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== current;
        step.setAttribute("aria-hidden", String(stepIndex !== current));
      });
      if (progressText) progressText.textContent = `Step ${current + 1} of ${steps.length}`;
      if (progressBar) progressBar.style.width = `${((current + 1) / steps.length) * 100}%`;
      setStatus(`Step ${current + 1} of ${steps.length}.`);
      if (moveFocus) {
        (query(".agency-form-progress", form) || form).scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
        steps[current]?.querySelector("input, select, textarea")?.focus({ preventScroll: true });
      }
    };

    const validateStep = () => {
      const fields = queryAll("input, select, textarea", steps[current]);
      const invalid = fields.find(field => !field.checkValidity());
      if (!invalid) return true;
      const details = invalid.closest("details");
      if (details) details.open = true;
      const message = showFieldError(invalid);
      setStatus(`Please review step ${current + 1}. ${message}`, false, "error");
      pushEvent("contact_form_validation_error", "project_form", { vc_step: current + 1, vc_field: fieldKey(invalid) });
      invalid.focus({ preventScroll: true });
      return false;
    };

    queryAll("[data-form-next]", form).forEach(button => button.addEventListener("click", () => {
      if (!validateStep()) return;
      pushEvent("contact_form_step", "project_form", { vc_step: current + 1, vc_direction: "next" });
      show(current + 1);
    }));
    queryAll("[data-form-back]", form).forEach(button => button.addEventListener("click", () => {
      pushEvent("contact_form_step", "project_form", { vc_step: current + 1, vc_direction: "back" });
      show(current - 1);
    }));
    form.addEventListener("input", event => {
      if (event.target.matches("input, select, textarea")) clearRelatedErrors(event.target);
    });
    form.addEventListener("change", event => {
      if (event.target.matches("input, select, textarea")) clearRelatedErrors(event.target);
    });
    form.addEventListener("submit", event => {
      const honeypot = query(".vc-honeypot", form);
      if (honeypot?.value || submitted) {
        event.preventDefault();
        return;
      }
      if (current < steps.length - 1) {
        event.preventDefault();
        if (validateStep()) {
          pushEvent("contact_form_step", "project_form", { vc_step: current + 1, vc_direction: "next" });
          show(current + 1);
        }
        return;
      }
      if (!form.checkValidity()) {
        event.preventDefault();
        const invalid = queryAll("input, select, textarea", form).find(field => !field.checkValidity());
        const invalidStep = steps.findIndex(step => step.contains(invalid));
        if (invalidStep >= 0) show(invalidStep, false);
        validateStep();
        return;
      }
      const submit = query('button[type="submit"]', form);
      recordEnquirySubmission(form);
      submitted = true;
      if (submit) {
        submit.disabled = true;
        submit.setAttribute("aria-busy", "true");
        submit.dataset.originalText = submit.textContent;
        submit.textContent = "Sending...";
      }
      setStatus("Sending your project enquiry.", true, "pending");
    });
    window.addEventListener("pageshow", event => {
      if (!event.persisted) return;
      submitted = false;
      const submit = query('button[type="submit"]', form);
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute("aria-busy");
        if (submit.dataset.originalText) submit.textContent = submit.dataset.originalText;
      }
      setStatus(`Step ${current + 1} of ${steps.length}.`);
    });
    const requestedService = new URLSearchParams(window.location.search).get("service");
    if (requestedService === "cro") {
      const serviceField = query("#project-service", form);
      if (serviceField) serviceField.value = "cro";
    } else if (requestedService === "product-question") {
      const parameters = new URLSearchParams(window.location.search);
      const serviceField = query("#project-service", form);
      const summaryField = query("#project-summary", form);
      if (serviceField) serviceField.value = "Not sure yet";
      if (summaryField && !summaryField.value) {
        const title = parameters.get("product") || "this product";
        const url = parameters.get("product_url") || "";
        summaryField.value = `I have a product question about ${title}.${url ? `\nProduct URL: ${url}` : ""}`;
      }
    }
    show(0, false);
  }

  function installAgencyStickyFit() {
    const panels = queryAll(".vc-agency-sidebar, .vc-service-sidebar, .vc-editorial-sidebar, .agency-faq-intro");
    if (!panels.length || !("ResizeObserver" in window)) return;
    const heights = new WeakMap();
    const desktop = window.matchMedia("(min-width: 761px)");
    const compactDesktop = window.matchMedia("(min-width: 761px) and (max-width: 1023px)");
    let frame = 0;
    const measure = () => {
      frame = 0;
      const availableHeight = window.innerHeight - (compactDesktop.matches ? 180 : 160) - 14;
      panels.forEach(panel => {
        const height = heights.get(panel) || 0;
        panel.classList.toggle("vc-sticky-fits", desktop.matches && height > 0 && height <= availableHeight);
      });
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    const observer = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
        heights.set(entry.target, box?.blockSize ?? entry.target.getBoundingClientRect().height);
      });
      schedule();
    });
    panels.forEach(panel => observer.observe(panel, { box: "border-box" }));
    window.addEventListener("resize", schedule, { passive: true });
  }

  function preserveViewportZoom() {
    // App embeds can append or rewrite viewport tags after the theme has loaded.
    // Keep the theme's responsive viewport while allowing visitors to magnify it.
    const restore = () => queryAll('meta[name="viewport"]', document.head).forEach(meta => {
      const content = "width=device-width, initial-scale=1";
      if (meta.content !== content) meta.content = content;
    });
    restore();
    const observer = new MutationObserver(records => {
      if (records.some(record => record.type === "childList" || record.target.matches('meta[name="viewport"]'))) restore();
    });
    observer.observe(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ["content", "name"] });
  }

  function start() {
    preserveViewportZoom();
    installImageFallbacks();
    installDesktopMegaMenus();
    installMobileNavigation();
    markCurrentNavigation();
    optimizeSiteConversions();
    installAnalyticsHooks();
    installProjectDateInputs();
    installCustomBudgets();
    installEnquiryVisibility();
    installFormSubmissionProtection();
    installAgencyAppDirectory();
    installAgencyContactForm();
    installAgencyStickyFit();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();
